import {
  Box,
  Grid,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
  TextField,
} from "@material-ui/core";
import Button from "components/CustomButtons/Button.js";
import React, { useState, useEffect } from "react";
import NotificationContext from "views/Components/Context.js";
import { replaceResourceAPI, fetchContentAPI } from "views/APIs/APIUtility";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import SaveIcon from "@material-ui/icons/Save";
import ExitReviewButton from "components/CustomButtons/ExitReviewButton";
import DoneIcon from "@material-ui/icons/Done";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { submitModuleForContentGeneration } from "views/APIs/APIUtility";
import Alert from "views/Components/Alert";
import ContentGenerationDialog from "./ContentGenerationDialog";
import OutlineEditor from "./OutlineEditor";
import DeepResearchControls from "./DeepResearchControls";
import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@material-ui/core";
import "katex/dist/katex.min.css";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  ...styles,
});

export default function ModuleInformationContentReview(props) {
  const classes = useStyles();
  // Extract the key and value of the top element in activeStatusData
  const [[activeStatusDataKey, activeStatusDataValue]] = Object.entries(
    props.activeStatusData
  );

  const { showNotification } = React.useContext(NotificationContext);
  const [moduleInformation, setModuleInformation] = useState("");
  const [loadingState, setLoadingState] = useState({
    isLoading: false,
    message: "",
  });
  const [module, setModule] = useState(props.module || null);
  const [showAlert, setShowAlert] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [mainOption, setMainOption] = useState("deepresearch");
  const [generateSpeakerNotes, setGenerateSpeakerNotes] = useState(false);
  const [outlineError, setOutlineError] = useState(null);
  const [maxWordsSpeakerNotesError, setMaxWordsSpeakerNotesError] = useState("");

  // Deep research control fields
  const [minBulletPointsSlide, setMinBulletPointsSlide] = useState(3);
  const [maxBulletPointsSlide, setMaxBulletPointsSlide] = useState(5);
  const [maxWordsBulletSlide, setMaxWordsBulletSlide] = useState(150);
  const [maxRows, setMaxRows] = useState(4);
  const [maxColumns, setMaxColumns] = useState(5);
  const [maxWordsPerCell, setMaxWordsPerCell] = useState(5);
  const [maxWordsTableSlide, setMaxWordsTableSlide] = useState(150);
  const [maxWordsSpeakerNotes, setMaxWordsSpeakerNotes] = useState(200);

  if (props.moduleInformation.length > 0) {
    const moduleInformationLink =
      props.moduleInformation[0].resource_link || "";
  } else {
    const moduleInformationLink = "";
  }

  const fetchFile = async (fileUrl) => {
    if (!fileUrl) {
      showNotification("Invalid file URL", "danger");
      return;
    }

    const formData = new FormData();
    formData.append("url", fileUrl);
    try {
      const data = await fetchContentAPI(formData);
      setModuleInformation(data.content);
    } catch (error) {
      console.error("Error: ", error);
      showNotification("Error fetching file", "danger");
    }
  };

  const setMIForCard = async () => {
    const card = props.moduleInformation[0];
    if (card && card.resource_link) {
      // fetchFile("https://qucoursify.s3.amazonaws.com/qu-course-design/673277707a469de58e0efa96/673277717a469de58e0efa9a/raw_resources/67337197c061106b9ae5f07a.md");
      await fetchFile(card.resource_link);
    } else {
      console.warn("Card or resource link not found");
    }
  };

  useEffect(() => {
    const fetchModuleInformation = async () => {
      setLoadingState({
        isLoading: true,
        message: "Loading Content Information...",
      });

      if (props.moduleInformation.length > 0) {
        await setMIForCard();
      }

      setLoadingState({ isLoading: false, message: "" });
    };

    fetchModuleInformation();
  }, [props.moduleInformation]);

  // Load deep research settings from module if available
  useEffect(() => {
    if (props.module) {
      const module = props.module;
      
      // Load deep research control fields if they exist
      if (module.min_bullet_points_slide !== undefined)
        setMinBulletPointsSlide(module.min_bullet_points_slide);
      if (module.max_bullet_points_slide !== undefined)
        setMaxBulletPointsSlide(module.max_bullet_points_slide);
      if (module.max_words_bullet_slide !== undefined)
        setMaxWordsBulletSlide(module.max_words_bullet_slide);
      if (module.max_rows !== undefined)
        setMaxRows(module.max_rows);
      if (module.max_columns !== undefined)
        setMaxColumns(module.max_columns);
      if (module.max_words_per_cell !== undefined)
        setMaxWordsPerCell(module.max_words_per_cell);
      if (module.max_words_table_slide !== undefined)
        setMaxWordsTableSlide(module.max_words_table_slide);
      if (module.max_words_speaker_notes !== undefined)
        setMaxWordsSpeakerNotes(module.max_words_speaker_notes);
      
      // Load generation options
      if (module.skip_content_generation !== undefined) {
        setMainOption(module.skip_content_generation === "True" ? "useoutline" : "deepresearch");
      }
      if (module.generate_speaker_notes !== undefined) {
        setGenerateSpeakerNotes(module.generate_speaker_notes === "True" || module.generate_speaker_notes === true);
      }
    }
  }, [props.module]);

  const handleModuleInformation = (content) => setModuleInformation(content);

  const handleMaxWordsSpeakerNotesChange = (value) => {
    const numValue = Number(value);
    setMaxWordsSpeakerNotes(numValue);
    if (numValue < 30 || numValue > 300) {
      setMaxWordsSpeakerNotesError("Must be between 30 and 300");
    } else {
      setMaxWordsSpeakerNotesError("");
    }
  };

  const handleSaveChanges = async () => {
    setLoadingState({ isLoading: true, message: "Saving changes..." });
    let resource_description = props.moduleInformation[0].resource_description;
    resource_description =
      resource_description + "###NOTE###" + moduleInformation;

    const formData = new FormData();
    formData.append("course_id", props.courseID);
    formData.append("module_id", props.moduleID);
    formData.append("resource_type", "Note");
    formData.append("resource_description", resource_description);
    formData.append("resource_id", props.moduleInformation[0].resource_id);
    formData.append("resource_name", props.moduleInformation[0].resource_name);
    formData.append("course_design_step", props.course_design_step);

    try {
      await replaceResourceAPI(formData);
      showNotification("Changes saved successfully", "success");
    } catch (error) {
      console.error("Error in handleUpdateNote:", error);
      showNotification("Error saving changes", "danger");
    } finally {
      setLoadingState({ isLoading: false, message: "" });
    }
  };

  const submitHandler = async () => {
    await handleSaveChanges();
    setLoadingState({
      isLoading: true,
      message: "Submitting Module for Content Generation...",
    });
    const formData = new FormData();
    formData.append("course_id", props.courseID);
    formData.append("module_id", props.moduleID);

    // Add parameters based on selected option
    if (mainOption === "deepresearch") {
      formData.append("skip_content_generation", "False");

      // Add deep research control parameters
      formData.append("min_bullet_points_slide", minBulletPointsSlide);
      formData.append("max_bullet_points_slide", maxBulletPointsSlide);
      formData.append("max_words_bullet_slide", maxWordsBulletSlide);
      formData.append("max_rows", maxRows);
      formData.append("max_columns", maxColumns);
      formData.append("max_words_per_cell", maxWordsPerCell);
      formData.append("max_words_table_slide", maxWordsTableSlide);
      formData.append("max_words_speaker_notes", maxWordsSpeakerNotes);
    } else if (mainOption === "useoutline") {
      formData.append("skip_content_generation", "True");
      if (generateSpeakerNotes) {
        formData.append("generate_speaker_notes", "True");
        formData.append("max_words_speaker_notes", maxWordsSpeakerNotes);
      }
    }

    try {
      const data = await submitModuleForContentGeneration(formData);
      setLoadingState({ isLoading: false, message: "" });
      showNotification(
        "Your job is submitted & review will be unlocked as soon as your request is processed.",
        "quSandBoxColor"
      );
    } catch (error) {
      showNotification("Error submitting module", "danger");
    } finally {
      window.location.reload();
    }
  };

  const verifyOutline = async () => {
    handleSaveChanges();
    if (props.moduleStatus !== "Outline Review") {
      setShowAlert(true);
    } else {
      setShowDialog(true);
    }
  };

  const handleConfirm = () => {
    setShowAlert(false); // Close the alert
    setShowDialog(true); // Show the dialog
  };

  const handleCancel = () => {
    setShowAlert(false); // Close the alert
  };

  const handleDialogConfirm = () => {
    setShowDialog(false);
    submitHandler();
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
  };

  const handleNext = async () => {
    props.setActiveTab("Slides");
  };

  if (loadingState.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loadingState.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      {showAlert && (
        <Alert
          title="Are you sure?"
          warning={true}
          showCancel={true}
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        >
          You already have a submission for this module. If you wish to make
          another submission, your module will be submitted for content review.
        </Alert>
      )}

      <ContentGenerationDialog
        open={showDialog}
        onClose={handleDialogCancel}
        onConfirm={handleDialogConfirm}
        mainOption={mainOption}
        setMainOption={setMainOption}
        generateSpeakerNotes={generateSpeakerNotes}
        setGenerateSpeakerNotes={setGenerateSpeakerNotes}
        minBulletPointsSlide={minBulletPointsSlide}
        setMinBulletPointsSlide={setMinBulletPointsSlide}
        maxBulletPointsSlide={maxBulletPointsSlide}
        setMaxBulletPointsSlide={setMaxBulletPointsSlide}
        maxWordsBulletSlide={maxWordsBulletSlide}
        setMaxWordsBulletSlide={setMaxWordsBulletSlide}
        maxRows={maxRows}
        setMaxRows={setMaxRows}
        maxColumns={maxColumns}
        setMaxColumns={setMaxColumns}
        maxWordsPerCell={maxWordsPerCell}
        setMaxWordsPerCell={setMaxWordsPerCell}
        maxWordsTableSlide={maxWordsTableSlide}
        setMaxWordsTableSlide={setMaxWordsTableSlide}
        maxWordsSpeakerNotes={maxWordsSpeakerNotes}
        setMaxWordsSpeakerNotes={setMaxWordsSpeakerNotes}
      />
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          {activeStatusDataValue === 1 ? (
            <></>
          ) : (
            // This section is for deliverables
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                marginTop: "20px",
              }}
            >
              {props.course_design_step === 11 && (
                <Button
                  color="success"
                  onClick={handleSaveChanges}
                  style={{ marginLeft: "auto" }}
                  startIcon={<SaveIcon />}
                >
                  Save Changes
                </Button>
              )}
            </div>
          )}
        </Grid>
        <Grid item xs={12} sm={12}>
          <OutlineEditor
            moduleInformation={moduleInformation}
            setModuleInformation={setModuleInformation}
            activeStatusDataValue={activeStatusDataValue}
            onErrorChange={setOutlineError}
          />
        </Grid>
        
        {/* Next Steps Section */}
        {activeStatusDataValue === 1 && (
          <Grid item xs={12} sm={12}>
            <Box mt={4}>
              <Typography variant="h5" gutterBottom>
                Next Steps
              </Typography>
              <Typography variant="body2" gutterBottom style={{ marginBottom: 16 }}>
                Choose how you want to generate slide content:
              </Typography>
              
              <RadioGroup
                value={mainOption}
                onChange={(e) => setMainOption(e.target.value)}
              >
                {/* Option 1: Deep Research */}
                <Box
                  mb={3}
                  p={2}
                  style={{ border: "2px solid #20677b", borderRadius: "8px" }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <FormControlLabel
                      value="deepresearch"
                      control={<Radio color="primary" style={{ padding: 0 }} />}
                      label={
                        <Typography variant="body1" style={{ color: "black", fontWeight: 500, marginLeft: "8px" }}>
                          Conduct deep research on each slide
                        </Typography>
                      }
                      style={{ alignItems: "flex-start", margin: 0 }}
                    />
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2" gutterBottom>
                            Generate detailed content with speaker notes for each slide
                          </Typography>
                          <Typography variant="caption">
                            ⏱ Takes 2-5 minutes per slide (30-40 minutes total for 30 slides)
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="left"
                    >
                      <IconButton size="small">
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  {mainOption === "deepresearch" && (
                    <DeepResearchControls
                      minBulletPointsSlide={minBulletPointsSlide}
                      setMinBulletPointsSlide={setMinBulletPointsSlide}
                      maxBulletPointsSlide={maxBulletPointsSlide}
                      setMaxBulletPointsSlide={setMaxBulletPointsSlide}
                      maxWordsBulletSlide={maxWordsBulletSlide}
                      setMaxWordsBulletSlide={setMaxWordsBulletSlide}
                      maxRows={maxRows}
                      setMaxRows={setMaxRows}
                      maxColumns={maxColumns}
                      setMaxColumns={setMaxColumns}
                      maxWordsPerCell={maxWordsPerCell}
                      setMaxWordsPerCell={setMaxWordsPerCell}
                      maxWordsTableSlide={maxWordsTableSlide}
                      setMaxWordsTableSlide={setMaxWordsTableSlide}
                      maxWordsSpeakerNotes={maxWordsSpeakerNotes}
                      setMaxWordsSpeakerNotes={setMaxWordsSpeakerNotes}
                    />
                  )}
                </Box>

                {/* Option 2: Use Outline */}
                <Box
                  p={2}
                  style={{ border: "2px solid #20677b", borderRadius: "8px" }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <FormControlLabel
                      value="useoutline"
                      control={<Radio color="primary" style={{ padding: 0 }} />}
                      label={
                        <Typography variant="body1" style={{ color: "black", fontWeight: 500, marginLeft: "8px" }}>
                          Convert the outline to slides directly
                        </Typography>
                      }
                      style={{ alignItems: "flex-start", margin: 0 }}
                    />
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="body2" gutterBottom>
                            Convert the outline directly to slides
                          </Typography>
                          <Typography variant="caption">
                            ⏱ Takes 3-10 minutes total
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="left"
                    >
                      <IconButton size="small">
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  {mainOption === "useoutline" && (
                    <Box ml={4} mt={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={generateSpeakerNotes}
                            onChange={(e) => setGenerateSpeakerNotes(e.target.checked)}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" style={{color: "black"}}>
                            Also generate speaker notes
                          </Typography>
                        }
                      />
                      {generateSpeakerNotes && (
                        <Box mt={2}>
                          <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>
                            Speaker Notes Settings
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Max words in speaker notes per slide"
                                type="number"
                                value={maxWordsSpeakerNotes}
                                onChange={(e) => handleMaxWordsSpeakerNotesChange(e.target.value)}
                                inputProps={{ min: 30, max: 300 }}
                                fullWidth
                                variant="outlined"
                                size="small"
                                error={!!maxWordsSpeakerNotesError}
                                helperText={maxWordsSpeakerNotesError}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </RadioGroup>
            </Box>
          </Grid>
        )}
        
        <Grid item xs={12} sm={12}>
          {props.course_design_step !== 11 && (
            <Grid container spacing={3} style={{ marginTop: "10px" }}>
              <Grid item xs={12} sm={4} style={{ textAlign: "left" }}>
                <ExitReviewButton courseID={props.courseID} />
              </Grid>
              <Grid item xs={12} sm={4} style={{ textAlign: "center" }} />
              <Grid item xs={12} sm={4} style={{ textAlign: "right" }}>
                {activeStatusDataValue === 1 ? (
                  <Button
                    color="info"
                    onClick={submitHandler}
                    style={{ float: "right" }}
                    endIcon={<DoneIcon />}
                    disabled={Boolean(outlineError)}
                  >
                    Generate Content
                  </Button>
                ) : (
                  <Button
                    color="quCreateColor"
                    onClick={handleNext}
                    style={{ float: "right" }}
                    endIcon={<NavigateNextIcon />}
                  >
                    Next
                  </Button>
                )}
                <Button
                  color="quCreateColor"
                  onClick={handleSaveChanges}
                  style={{ float: "right", marginLeft: "10px" }}
                  startIcon={<SaveIcon />}
                >
                  Save Changes
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </div>
  );
}
